package com.ijoin

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import com.ijoin.analytics.PixelTracker
import com.ijoin.deeplink.DeepLinkHandler
import com.ijoin.push.WebPushInitializer
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.PromiseRejectionEvent
import org.w3c.dom.events.Event

private var composeStarted = false

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    // Suppress AbortError console messages from Ktor when SSE streams are cancelled
    suppressAbortErrors()

    val ready = document.readyState.toString().lowercase()
    if (ready == "complete" || ready == "interactive") {
        startCompose()
    } else {
        window.addEventListener("DOMContentLoaded", { startCompose() })
    }
}

private fun suppressAbortErrors() {
    window.addEventListener("unhandledrejection", { event ->
        val rejection = event as? PromiseRejectionEvent ?: return@addEventListener
        val message = rejection.reason?.let { reason ->
            when (reason) {
                is Throwable -> reason.message ?: ""
                else -> reason.toString()
            }
        } ?: ""
        if (message.contains("AbortError") || message.contains("BodyStreamBuffer was aborted")) {
            rejection.preventDefault()
        }
    })
}

@OptIn(ExperimentalComposeUiApi::class)
private fun startCompose() {
    if (composeStarted) return
    composeStarted = true

    // Log version info for debugging cache issues
    println("ijoin v${BuildConfig.VERSION_NAME} (build: ${BuildConfig.BUILD_TIMESTAMP})")

    PixelTracker.init()
    WebPushInitializer.initialize()

    // Handle initial URL deep link
    handleCurrentUrl()

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", { _: Event ->
        handleCurrentUrl()
    })

    val container = ensureComposeContainer() ?: return
    ComposeViewport(container) {
        App()
    }
}

private fun handleCurrentUrl() {
    val url = window.location.href
    DeepLinkHandler.handleUrl(url)
    PixelTracker.trackPageView()
}

private fun ensureComposeContainer(): HTMLElement? {
    val body = document.body ?: return null
    val existing = document.getElementById("compose-root") as? HTMLElement
    if (existing != null) {
        return existing
    }
    val host = (document.createElement("div") as HTMLElement).apply {
        id = "compose-root"
        style.apply {
            setProperty("position", "fixed")
            setProperty("inset", "0")
        }
    }
    body.appendChild(host)
    return host
}
